/**
 * StandaloneWidgetView.jsx — Fullscreen dedicated widget view.
 *
 * Eliminates the entire desktop frame/window chrome and renders
 * a single prioritized widget full-viewport with user themes and settings.
 *
 * Accessible via:
 * - URL Hash: /#widgetType or /#widget=widgetType (e.g. /#fissal-ai, /#price-check, /#raffle)
 * - Domain routing: fissal.ech-o.net -> renders Fissal AI Terminal fullscreen directly.
 */

import { createSignal, onMount, onCleanup, Show } from 'solid-js';
import { rendererFor } from '../widgets/index.jsx';
import wm from '../lib/wmStore.js';
import './StandaloneWidgetView.css';

export function getStandaloneWidgetType() {
  if (typeof window === 'undefined') return null;

  // 1. Domain-based routing: fissal.ech-o.net -> Fissal Terminal
  const hostname = window.location.hostname.toLowerCase();
  if (hostname.includes('fissal')) {
    return 'fissal-open-webui';
  }

  // 2. Hash-based routing: #fissal-ai, #price-check, #kiosks, #widget=price-check, #raffle, etc.
  const hash = window.location.hash.replace(/^#\/?/, '').trim();
  if (hash) {
    if (hash.startsWith('widget=')) {
      return hash.split('=')[1].split('?')[0].trim();
    }
    // direct tag like #fissal-open-webui or #price-check or #raffle?week=...
    const cleanTag = hash.split('?')[0].split('&')[0].trim();
    if (cleanTag !== 'top' && cleanTag !== '') {
      return cleanTag;
    }
  }

  // 3. Query param fallback: ?widget=fissal-open-webui or ?standalone=price-check
  const params = new URLSearchParams(window.location.search);
  const qWidget = params.get('widget') || params.get('standalone');
  if (qWidget) return qWidget.split('?')[0].trim();

  return null;
}

export default function StandaloneWidgetView(props) {
  const { widgetType, onExit } = props;
  const Body = rendererFor(widgetType);
  const meta = () => wm.state.catalog.find(c => c.type === widgetType) || { title: widgetType, emoji: '⚡' };

  // Find existing instance in active dashboard to share settings/state, or create stable default
  const existingWidget = () => {
    const dash = wm.state.dashboards.find(d => d.id === wm.state.activeDashboardId);
    return dash?.widgets.find(w => w.widget_type === widgetType) || {
      id: `standalone_${widgetType}`,
      widget_type: widgetType,
      settings: {},
      maximized: true,
    };
  };

  const isFissalDomain = () => typeof window !== 'undefined' && window.location.hostname.toLowerCase().includes('fissal');

  return (
    <div class="v2-standalone-view">
      <div class="v2-standalone-view__header">
        <div class="v2-standalone-view__brand">
          <span class="v2-standalone-view__emoji">{meta().emoji || '⚡'}</span>
          <span class="v2-standalone-view__title">{meta().title || widgetType}</span>
          <span class="v2-standalone-view__badge">FULLSCREEN MODE</span>
        </div>

        <div class="v2-standalone-view__actions">
          <Show when={!isFissalDomain()}>
            <button
              class="v2-btn-sm v2-standalone-view__exit-btn"
              onClick={onExit}
              title="Return to Desktop View"
            >
              ◱ Desktop Mode
            </button>
          </Show>
          <Show when={isFissalDomain()}>
            <a
              class="v2-btn-sm v2-standalone-view__exit-btn"
              href="https://redfur.ech-o.net"
              title="Open RedfurBot Full Dashboard"
            >
              ◱ Redfur Dashboard
            </a>
          </Show>
        </div>
      </div>

      <div class="v2-standalone-view__content">
        <Body
          widget={existingWidget()}
          settings={existingWidget().settings}
          maximized={true}
          identity={wm.state.viewer}
        />
      </div>
    </div>
  );
}